import React, { useRef, useEffect } from "react";
import "./StepNavigation.scss";

/**
 * Step navigation component for multi-step forms.
 * Uses shared CheckCircleFilledIcon for completed steps.
 */
const StepNavigation = ({
  steps = [],
  currentStep = 0,
  onStepClick,
  completedSteps = [],
}) => {

    const containerRef = useRef(null);
const stepRefs = useRef([]);

useEffect(() => {
  if (stepRefs.current[currentStep]) {
    stepRefs.current[currentStep].scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest"
    });
  }
}, [currentStep]);

  if (!steps || steps.length <= 1) {
    return null; // Don't show navigation for single-step forms
  }

  const renderStepIndicator = (isCompleted, isActive) => {
    if (isCompleted) {
      return (
        <div className="step-dot">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="8" fill="#03BF43" />
            <path
              d="M10.9068 5.5956C10.7898 5.47855 10.6002 5.47855 10.4835 5.5956L6.50329 9.57577L5.51698 8.59013C5.40027 8.47309 5.21034 8.47309 5.09364 8.59013C4.9766 8.70716 4.9766 8.89677 5.09364 9.01347L6.25415 10.174C6.28495 10.2048 6.32123 10.2263 6.35955 10.2411C6.40713 10.2702 6.4588 10.2897 6.51253 10.2897C6.58918 10.2897 6.66585 10.2606 6.72437 10.2021L10.9068 6.01928C11.0238 5.90225 11.0238 5.71265 10.9068 5.59561V5.5956H10.9068Z"
              fill="white"
            />
          </svg>
        </div>
      );
    }
    if (isActive) {
      return (
        <div className="step-dot active">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="8" fill="#FCE4E5" />
            <circle cx="7.99996" cy="8.0013" r="5.33333" fill="#F6ABAE" />
            <circle cx="7.99996" cy="8.0013" r="3.33333" fill="#97262A" />
          </svg>
        </div>
      );
    }
    return (
      <div className="step-dot">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="8" cy="8" r="8" fill="#AFB0B1" />
        </svg>
      </div>
    );
  };

  return (
    <nav  ref={containerRef} className="step-navigation" aria-label="Form progress">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = index === currentStep;
        const isCompleted = completedSteps.includes(index);
        const isClickable = onStepClick && (isCompleted || index < currentStep);
        const stepKey =
          step.id || step.name || step.fieldReference || `step-${index}`;

        return (
          <div
            ref={(el) => (stepRefs.current[index] = el)}
            key={stepKey}
            className={`step-item ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""} ${isClickable ? "clickable" : ""}`}
          >
            <div className="step-indicator">
              {renderStepIndicator(isCompleted, isActive)}
            </div>
            <div className="step-label">
              {step.label || step.title || `Step ${stepNumber}`}
              {isClickable &&
                <button
                  className="edit-modal-btn"
                  role={isClickable ? "button" : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  aria-current={isActive ? "step" : undefined}
                  aria-label={`Step ${stepNumber}: ${step.label || step.title || `Step ${stepNumber}`}${isCompleted ? " (completed)" : ""}${isActive ? " (current)" : ""}`}
                  onClick={isClickable ? () => onStepClick(index) : undefined}
                  onKeyDown={
                    isClickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onStepClick(index);
                          }
                        }
                      : undefined
                  }
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.2069 3.0861L9.41438 0.292974C9.32152 0.200091 9.21127 0.126411 9.08993 0.0761423C8.96859 0.0258733 8.83853 0 8.70719 0C8.57585 0 8.4458 0.0258733 8.32446 0.0761423C8.20312 0.126411 8.09287 0.200091 8 0.292974L0.29313 8.00047C0.199867 8.093 0.125926 8.20313 0.0756045 8.32448C0.025283 8.44584 -0.000414649 8.57598 5.05934e-06 8.70735V11.5005C5.05934e-06 11.7657 0.105362 12.02 0.292898 12.2076C0.480435 12.3951 0.734788 12.5005 1 12.5005H3.79313C3.9245 12.5009 4.05464 12.4752 4.17599 12.4249C4.29735 12.3746 4.40748 12.3006 4.5 12.2073L12.2069 4.50047C12.2998 4.40761 12.3734 4.29736 12.4237 4.17602C12.474 4.05468 12.4999 3.92463 12.4999 3.79329C12.4999 3.66195 12.474 3.53189 12.4237 3.41055C12.3734 3.28921 12.2998 3.17896 12.2069 3.0861ZM3.79313 11.5005H1V8.70735L6.5 3.20735L9.29313 6.00047L3.79313 11.5005ZM10 5.29297L7.20688 2.50047L8.70688 1.00047L11.5 3.79297L10 5.29297Z"
                      fill="#97262A"
                    />
                  </svg>
                </button>
              }
            </div>
          </div>
        );
      })}
    </nav>
  );
};

export default React.memo(StepNavigation);
